import type { FormatterDef, FormatterFn, FormatOptions } from './types';
import { formatNumber, formatDelta } from './number';

export class FormatterRegistry<T = unknown> {
  private formatters = new Map<string, FormatterDef<T>>();

  register(name: string, formatter: FormatterDef<T> | FormatterFn<T>): void {
    const def: FormatterDef<T> = typeof formatter === 'function'
      ? { name, format: formatter as FormatterFn<T> }
      : formatter;

    this.formatters.set(name, def);
  }

  get(name: string): FormatterFn<T> | undefined {
    return this.formatters.get(name)?.format;
  }

  has(name: string): boolean {
    return this.formatters.has(name);
  }

  list(): string[] {
    return Array.from(this.formatters.keys());
  }

  format(name: string, value: T, options?: FormatOptions): string {
    const formatter = this.get(name);

    if (!formatter) {
      return String(value);
    }

    return formatter(value, options);
  }

  static createWithDefaults<T = unknown>(): FormatterRegistry<T> {
    const registry = new FormatterRegistry<T>();

    registry.register('number', (value, options?) => formatNumber(value as number, options));
    registry.register('compact', (value) => formatNumber(value as number, { notation: 'compact' }));
    registry.register('delta', (value, options?) => formatDelta(value as number, options));

    return registry;
  }
}
